import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import S3Upload from "react-s3-uploader/s3upload";
import AuthService from "services/auth.service";
import shortid from "shortid";
import { createWidget, copyWidgets, cutWidgets, actionGroup, deleteWidgets, updateBulkWidgetProperty, updateWidgetProperty, copyPasteAnimation } from "actions/editor";
import { initiateMediaUpload, updateMediaUploadProgress, uploadMedia } from "actions/media";
import { getPositionForNewWidget } from "components/Editor/helper/layoutManager";
import { FailureToast, SuccessToast } from "components/Toast";

import { EditorContext } from "components/Editor";
import StoryboardContext from "components/Storyboard/context";
import { useSelectedWidgetSelector } from "components/Editor/hooks/useEditorSession";

import _ from "lodash";
import { Frame } from "scenejs";
import { toast } from "react-toastify";

import { ANCHOR, ASSET, BUTTON, CAROUSEL, CAROUSEL_CARD, CONTENT, CONTENT_CARD, CONTENT_FILTER, CONTENT_FILTER_CONTAINER, CONTENT_SEARCH, IMAGE, LINE, LIVE_ELEMENT, MENU, SECTION, SECTION_COLUMN, SEEK, SHAPE, VIDEO, HOTSPOT, TEXT, MENU_CONTAINER, MENU_DRAGGABLE_CONTAINER } from "constants/widgetTypes";

const API_URL = `${process.env.REACT_APP_PAPERFLITE_API_URL}/api/2.0`;

const clipboardManagerState = {
  desktopParentLayout: null,
  mobileParentLayout: null,
  defaultParent: null
};

export default function ClipboardManager(props) {
  const dispatch = useDispatch();

  const lastCallRef = useRef(null);
  const lastPasteCallRef = useRef(null);
  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext?.editorSession?.id;
  const storyboardContext = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboardContext, 'type');
  const workspace = useSelector(state => _.get(state, 'workspaces.workspace.item', {}));
  const entityType = useSelector(state => _.get(state, `${editorContext.editorSession.id}.present.entityType`));

  const activePageSection = useSelector(state => {
    if((storyboardType === 'LANDING_PAGE' || storyboardType === 'RESOURCE_CENTER') && entityType!="GATING") {
      const selectedPage = _.get(state, `${editorContext.editorSession.id}.present.selectedPage`, null);
      const activePage = _.get(state, `${editorContext.editorSession.id}.present.activeSegment`, null);
      return selectedPage !== activePage ? activePage : selectedPage;
    }

    return _.get(state, `${editorContext.editorSession.id}.present.interactive.active`, null);
  });
  const breakpoint = useSelector(state => _.get(state, `${editorContext.editorSession.id}.present.breakpoint`));
  const uploads = useSelector(state => _.get(state, 'media.uploads')) || [];
  const defaultParent = activePageSection;
  const parentWidget = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || []
    return widgets.find(widget => {
      return widget.uid === defaultParent
    });
  });
  const selectedWidgets = useSelectedWidgetSelector(editorSession);

  const pageId = editorSession?.split("_")?.[1] || '';
  
  const pasteStateReference = React.useRef({
    page: null,
    scrollPosition: null,
    offset: 1
  });

  useEffect(() => {
    editorContext.editorSession.eventBus.on('copy', handleCopy);
    editorContext.editorSession.eventBus.on('cut', handleCut);
    editorContext.editorSession.eventBus.on('paste', handlePaste);
    editorContext.editorSession.eventBus.on('pasteMedia', handlePasteMedia);
    editorContext.editorSession.eventBus.on('dropMedia', handlePasteMedia);
    editorContext.editorSession.eventBus.on('copyAnimation', handleCopyAnimation);
    editorContext.editorSession.eventBus.on('pasteAnimation', handlePasteAnimation);

    return () => {
      editorContext.editorSession.eventBus.off('copy', handleCopy);
      editorContext.editorSession.eventBus.off('cut', handleCut);
      editorContext.editorSession.eventBus.off('paste', handlePaste);
      editorContext.editorSession.eventBus.off('pasteMedia', handlePasteMedia);
      editorContext.editorSession.eventBus.off('dropMedia', handlePasteMedia);
      editorContext.editorSession.eventBus.off('copyAnimation', handleCopyAnimation);
      editorContext.editorSession.eventBus.off('pasteAnimation', handlePasteAnimation);
    }

  }, [activePageSection]);

  useEffect(() => {
    const desktopParentLayout = _.get(parentWidget, 'properties.desktop.layout');
    const mobileParentLayout = _.get(parentWidget, 'properties.mobile.layout');

    if (defaultParent != null && desktopParentLayout != null && mobileParentLayout != null) {
      clipboardManagerState.desktopParentLayout = desktopParentLayout;
      clipboardManagerState.mobileParentLayout = mobileParentLayout;
      clipboardManagerState.defaultParent = defaultParent;
    }
    
  }, [defaultParent, parentWidget, editorSession]);

  useEffect(() => {
    if (uploads && uploads.length > 0) {
      uploads.forEach(upload => {
        if (upload.error) {
          const errorDescription = 'Unable to upload the file, Please try again.';
        
          if (!toast.isActive(`upload-error-${upload.id}-toast`)) {
            toast(
              <FailureToast
                heading="Upload Failed"
                description={errorDescription}
              />,
              {
                type: toast.TYPE.DEFAULT,
                position: toast.POSITION.BOTTOM_RIGHT,
                autoClose: 3000,
                toastId: `upload-error-${upload.id}-toast`
              }
            );
          }
          dispatch(deleteWidgets(editorSession, [`widget_loading_${upload.id}`]));
        }
      });
    }
  }, [uploads, editorSession]);

  const handleCopy = (e) => {
    const request = prepareCopyRequest();
    dispatch(copyWidgets(editorContext.editorSession.id, request));
    pasteStateReference.current = {
      page: activePageSection,
      scrollPosition: request.scrollPosition,
      offset: 1
    };
  }

  const handleCut = (e) => {
    const request = prepareCopyRequest();
    dispatch(cutWidgets(editorContext.editorSession.id, request));
    pasteStateReference.current = {
      page: activePageSection,
      scrollPosition: request.scrollPosition,
      offset: 1
    };
  }

  const handleFileCheck = (file,next) => {
    next(file);
  }

  const getDimensions = (file) => {
    return new Promise((resolve) => {
      if (file.type.startsWith('image/')) {
        const img = new Image();
        img.onload = () => {
          resolve({ width: img.naturalWidth, height: img.naturalHeight });
          URL.revokeObjectURL(img.src);
        };
        img.onerror = () => resolve({ width: 600, height: 600 });
        img.src = URL.createObjectURL(file);
      } else {
        resolve({ width: 600, height: 600 }); 
      }
    });
  };

  const handleUploadComplete = async (info, file, loadingWidgetId, desktopPosition, mobilePosition) => {
    const formats = ['image/jpg', 'image/jpeg', 'image/png', 'image/svg', 'image/bmp', 'image/gif', 'image/svg+xml'];
  
    if (info && formats.includes(file.type)) {
      const dimensions = await getDimensions(file);
      const width = dimensions.width;
      const height = dimensions.height;
  
      const request = {
        fileName: file.name,
        contentType: file.type,
        signedUrl: info.signedUrl,
        width: width,
        height: height,
        storyboard: _.get(storyboardContext, 'id'),
      };
  
      handleUploadMedia(request, file, loadingWidgetId, desktopPosition, mobilePosition);
    } else {
      setTimeout(() => {
        dispatch(deleteWidgets(editorSession, [loadingWidgetId]));
      }, 0);
    }
  };

  const handleCreateImage = (medias, specificPositions = null) => {
    actionGroup.start();
    let successfulUploads = 0;

    medias.forEach((media, index) => {
      const naturalWidth = _.get(media, 'width') || 600;
      const naturalHeight = _.get(media, 'height') || 600;
  
      const desktopWidth = Math.min(600, naturalWidth);
      const desktopHeight = desktopWidth * naturalHeight / naturalWidth;
      const mobileWidth = Math.min(300, naturalWidth);
      const mobileHeight = mobileWidth * naturalHeight / naturalWidth;
  
      let desktopPosition, mobilePosition;
  
      if (specificPositions && specificPositions[index]) {
        desktopPosition = specificPositions[index].desktop;
        mobilePosition = specificPositions[index].mobile;
      } else {
        desktopPosition = getPositionForNewWidget(editorSession, clipboardManagerState.desktopParentLayout, { width: desktopWidth, height: desktopHeight });
        mobilePosition = getPositionForNewWidget(editorSession, clipboardManagerState.mobileParentLayout, { width: 300, height: desktopHeight });
      }
      
      let widget = null;
      if (media.type === 'image') {
        widget = {
          type: IMAGE,
          name: "Image",
          properties: {
            desktop: {
              visible: true,
              layout: {
                transform: {
                  translate: `${desktopPosition.x}px, ${desktopPosition.y}px`,
                  rotate: "0deg"
                },
                width: `${desktopWidth}px`,
                height: `${desktopHeight}px`
              },
              crop: null,
              alt: null,
              animation: null,
              border: null,
              position: null,
              shadow: null
            },
            mobile: {
              visible: true,
              layout: {
                transform: {
                  translate: `${mobilePosition.x}px, ${mobilePosition.y}px`,
                  rotate: "0deg"
                },
                width: `${mobileWidth}px`,
                height: `${mobileHeight}px`
              },
              crop: null,
              alt: null,
              animation: null,
              border: null,
              position: null,
              shadow: null
            }
          },
          content: media
        };
      }
      
      if (widget) {
        dispatch(createWidget(editorSession, [widget], clipboardManagerState.defaultParent));
        successfulUploads++;
      }
    });

    const isToastAlreadyActive = toast.isActive('image_uploaded_toast');
    if (!isToastAlreadyActive && uploads.length == 0 && successfulUploads > 0) {
      toast(
        <SuccessToast
          heading={`Success`}
          description={`Image Uploaded successfully`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
          toastId: 'image_uploaded_toast'
        })
    }

    actionGroup.end();
  };

  const handleUploadMedia = (request, file, loadingWidgetId, desktopPosition, mobilePosition) => {
    dispatch(uploadMedia(file.id, request, (response) => {
      if (response) {
        const specificPositions = [{
          desktop: desktopPosition,
          mobile: mobilePosition
        }];
        handleCreateImage([response], specificPositions);
      }
  
      setTimeout(() => {
        dispatch(deleteWidgets(editorSession, [loadingWidgetId]));
      }, 10);
    }, true));
  };

  const removeSpecialCharactersFromName = (name)=>{
    return name.replace(/\u202F/g, '');
  }

  const handleUploadProgress = (progress, textState, file) => {
    dispatch(updateMediaUploadProgress(file.id, progress));
  }

  const handleUploadStart = (instances) => {
    instances.forEach(instance => {
      const upload = {
        id: instance.files[0].id,
        name: instance.files[0].name,
        type: instance.files[0].type,
        size: (instance.files[0].size / 1048576),
        progress: 0,
        instance: instance
      }

      dispatch(initiateMediaUpload(upload, false, true));
    });

  }

  const handlePasteMedia = async (instances) => {
    const currentTime = Date.now();
    if (lastCallRef.current && currentTime - lastCallRef.current < 100) {
      return;
    }
    lastCallRef.current = currentTime;
  
    const files = instances.files || [];
    
    if (files.length === 0) {
      return;
    }
  
    const imageFormats = ['image/jpg', 'image/jpeg', 'image/png', 'image/svg', 'image/bmp', 'image/gif', 'image/svg+xml'];
    const validImageFiles = files.filter(file => imageFormats.includes(file.type));
    const invalidFiles = files.filter(file => !imageFormats.includes(file.type));

    if (invalidFiles.length > 0) {
      invalidFiles.forEach(file => {
        const errorDescription = `${file.type || 'Unknown'} file type is not supported`;
      
        if (!toast.isActive(`unsupported-${file.type}-toast`)) {
          toast(
            <FailureToast
              heading="Failure"
              description={errorDescription}
            />,
            {
              type: toast.TYPE.DEFAULT,
              position: toast.POSITION.BOTTOM_RIGHT,
              autoClose: 3000,
              toastId: `unsupported-${file.type}-toast`
            }
          );
        }
      });
    }

    if (validImageFiles.length === 0) {
      return;
    }
  
    const scrollContainer = document.querySelector(`#${editorContext.editorSession.id} .editor_widget_scroll_container`);
    const currentScrollPosition = {
      top: scrollContainer.scrollTop,
      left: scrollContainer.scrollLeft
    };
  
    var lastPastedPage = pasteStateReference.current.page;
    var offset = pasteStateReference.current.offset;
    if(lastPastedPage != activePageSection || viewportPositionChanged(currentScrollPosition, pasteStateReference.current.scrollPosition)){
      offset = 0;
    }
  
    const processedUploads = [];
    
    for (let i = 0; i < validImageFiles.length; i++) {
      const file = validImageFiles[i];
      file.id = shortid();
      
      const updatedName = removeSpecialCharactersFromName(file.name);
      const dimensions = await getDimensions(file);
  
      const naturalWidth = dimensions.width;
      const naturalHeight = dimensions.height;
      const desktopWidth = Math.min(600, naturalWidth);
      const desktopHeight = desktopWidth * naturalHeight / naturalWidth;
      const mobileWidth = Math.min(300, naturalWidth);
      const mobileHeight = mobileWidth * naturalHeight / naturalWidth;
  
      const desktopPosition = getPositionForNewWidget(
        editorSession, 
        clipboardManagerState.desktopParentLayout, 
        { width: desktopWidth, height: desktopHeight }
      );
      const mobilePosition = getPositionForNewWidget(
        editorSession, 
        clipboardManagerState.mobileParentLayout, 
        { width: 300, height: desktopHeight }
      );
  
      const totalOffset = (offset + i) * 20;
      desktopPosition.x += totalOffset;
      desktopPosition.y += totalOffset;
      mobilePosition.x += totalOffset;
      mobilePosition.y += totalOffset;
  
      const loadingWidgetId = `widget_loading_${file.id}`;
      
      const loadingWidget = {
        type: "cleverstory.v2.widgets.image",
        name: "Image",
        properties: {
          desktop: {
            visible: true,
            layout: {
              transform: {
                translate: `${desktopPosition.x}px, ${desktopPosition.y}px`,
                rotate: "0deg"
              },
              width: `${desktopWidth}px`,
              height: `${desktopHeight}px`
            },
            crop: null,
            alt: null,
            animation: null,
            border: null,
            position: null,
            shadow: null
          },
          mobile: {
            visible: true,
            layout: {
              transform: {
                translate: `${mobilePosition.x}px, ${mobilePosition.y}px`,
                rotate: "0deg"
              },
              width: `${mobileWidth}px`,
              height: `${mobileHeight}px`
            },
            crop: null,
            alt: null,
            animation: null,
            border: null,
            position: null,
            shadow: null
          }
        },
        content: null,
        uid: loadingWidgetId,
        parent: `${clipboardManagerState.defaultParent}`
      };
  
      dispatch(createWidget(editorSession, [loadingWidget], clipboardManagerState.defaultParent, true, true));
  
      const s3Uploader = new S3Upload({
        signingUrl: "/file_upload/initiate",
        signingUrlMethod: "POST",
        signingUrlHeaders: {
          Authorization: `Bearer ${AuthService.getAccessToken()}`
        },
        server: API_URL,
        scrubFilename: (filename) => {
          let name = removeSpecialCharactersFromName(filename);
          name = name.replace(/[^\w\d_\-.\s]+/ig, '');
          return name;
        },
        signingUrlQueryParams: { 'originalName': encodeURIComponent(updatedName) },
        contentDisposition: 'auto',
        preprocess: handleFileCheck,
        uploadRequestHeaders: {},
        onFinishS3Put: (info, uploadedFile) => handleUploadComplete(info, uploadedFile, loadingWidgetId, desktopPosition, mobilePosition),
        onProgress: handleUploadProgress,
        onError: (error) => {
          const errorDescription = 'Unable to upload the file, Please try again, Please try again.';

          if (!toast.isActive(`upload-error-${file.id}-toast`)) {
            toast(
              <FailureToast
                heading="Upload Failed"
                description={errorDescription}
              />,
              {
                type: toast.TYPE.DEFAULT,
                position: toast.POSITION.BOTTOM_RIGHT,
                autoClose: 3000,
                toastId: `upload-error-${file.id}-toast`
              }
            );
          }

          setTimeout(() => {
            dispatch(deleteWidgets(editorSession, [loadingWidgetId]));
          }, 0);
        },
        files: [file]
      });
  
      processedUploads.push(s3Uploader);
    }
  
    pasteStateReference.current = {
      page: activePageSection,
      scrollPosition: currentScrollPosition,
      offset: offset + validImageFiles.length
    };
  
    handleUploadStart(processedUploads);
  };

  const handlePaste = (e) => {
    const currentTime = Date.now();
    
    if (lastPasteCallRef.current && currentTime - lastPasteCallRef.current < 300) {
      return;
    }

    lastPasteCallRef.current = currentTime;

    const clipboardData = window.localStorage.getItem('CLIPBOARD');
    if(!clipboardData){
      return;
    }

    var request = JSON.parse(clipboardData);
    var widgets = preparePasteRequest(activePageSection, request);

    if(!widgets) {
      return;
    }

    dispatch(createWidget(editorContext.editorSession.id, widgets, activePageSection));
  }

  const handleCopyAnimation = ({ exit }) => {
    const request = {
      type: 'copy', 
      exit,
      storyboardId: _.get(storyboardContext, 'id'),
      workspaceId: _.get(workspace, 'id', null),
      editorSession: editorContext.editorSession.id, 
    };

    dispatch(copyPasteAnimation(request, (type) => {
      if(type === 'copy') {
        toast(
          <SuccessToast
            heading={`Success`}
            description={`Animation copied.`}
          />,
          {
            type: toast.TYPE.DEFAULT,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
          }
        );
      }
    }));
  };
  
  const handlePasteAnimation = ({ exit }) => {
    const request = {
      type: 'paste',
      exit,
      editorSession: editorContext.editorSession.id,
    };

    dispatch(copyPasteAnimation(request, (type) => {
      if(type === 'paste') {
        toast(
          <SuccessToast
            heading={`Success`}
            description={`Animation pasted.`}
          />,
          {
            type: toast.TYPE.DEFAULT,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
          }
        );
      }
    }));
  };

  const prepareCopyRequest = () => {
    const scrollContainer = document.querySelector(`#${editorContext.editorSession.id} .editor_widget_scroll_container`);

    var request = {
      storyboard: _.get(storyboardContext, 'id'),
      workspace: _.get(workspace, 'id', null),
      scrollPosition: {
        top: scrollContainer.scrollTop,
        left: scrollContainer.scrollLeft
      },
      activePageSection,
      pageId
    }

    return request;
  }

  const preparePasteRequest = (pageWidget, request) => {
    const scrollContainer = document.querySelector(`#${editorContext.editorSession.id} .editor_widget_scroll_container`);
    const top = scrollContainer.scrollTop;
    const left = scrollContainer.scrollLeft;
    const storyboardType = _.get(storyboardContext, 'type');
    const currentStoryboardId = _.get(storyboardContext, 'id');
    const variableMappingsIgnoreTypes = ['LIVE_ELEMENT', 'GATING', 'PALETTE_COMPONENT'];
    const originalTop = _.get(request, 'scrollPosition.top', 0);
    const originalLeft = _.get(request, 'scrollPosition.left', 0);
    
    const currentScrollPosition = {
      top: top,
      left: left
    }

    var lastPastedPage = pasteStateReference.current.page;
    var offset = pasteStateReference.current.offset;
    if(lastPastedPage != pageWidget || viewportPositionChanged(currentScrollPosition, pasteStateReference.current.scrollPosition)){
      offset = 0;
    }

    var widgets = request.widgets || [];
   
    var ignoreTypes = [];
    switch (true) {
      case entityType === 'GATING':
        ignoreTypes = [MENU, SEEK, CONTENT, SECTION, SECTION_COLUMN, ASSET, CAROUSEL, ANCHOR]
        break;
      case entityType === 'LIVE_ELEMENT':
        ignoreTypes = [...ignoreTypes, LIVE_ELEMENT, CONTENT, CAROUSEL, ANCHOR, SEEK, ASSET];
        break;
      case entityType === 'PALETTE_COMPONENT':
        ignoreTypes = [ANCHOR]
        break;
      case storyboardType === 'INTERACTIVE_CONTENT':
        ignoreTypes = [MENU];
        if (request.widgets[0] && request.widgets[0].properties[breakpoint].stretched) {
          ignoreTypes = [...ignoreTypes, request.widgets[0].type];
        }
        break;
      case storyboardType === 'SPATIAL':
        ignoreTypes = [MENU, SEEK, CONTENT, SECTION_COLUMN, ANCHOR, ];
        if (request.widgets[0] && request.widgets[0].properties[breakpoint].stretched) {
          ignoreTypes = [...ignoreTypes, request.widgets[0].type];
        }
        break;
      default:
        break;
    }
    
    if (workspace.id !== request.workspace) {
      ignoreTypes = [...ignoreTypes, LIVE_ELEMENT]
    }
    
    widgets = widgets.filter(item => !_.includes(ignoreTypes, item.type));

    widgets = widgets.map(widget => {
      const allowedActions = ['link', 'pageAnchor'];
      const getActionType = (widget) => {
        const actions = _.get(widget, 'actions' , []);
        const actionType = _.last(actions)?.type;
        return actionType;
      }
      let children = _.get(widget, 'widgets', null);
      let hotspot = children && children.find(item => _.isEqual(HOTSPOT, item.type));

      if (hotspot && entityType === 'GATING'){
        const hotspotChildren = _.get(hotspot, 'widgets', null);
        if (_.size(hotspotChildren) > 0){
          const carouselChild = hotspotChildren.filter(item => item.type === CAROUSEL);
          if (carouselChild){
            toast(
              <FailureToast
                heading="Pasting Failed"
                description="Hotspot with a carousel isn’t supported in gating screens."
              />,
              {
                type: toast.TYPE.DEFAULT,
                position: toast.POSITION.BOTTOM_RIGHT,
                autoClose: 3000
              }
            );
            return;
          } 
        }
      }

      if(children != null && !hotspot) {
        children = children.map(childwidget => {
          if(_.get(childwidget, 'actions') && _.get(childwidget, 'actions') !== null) {
            if(_.get(request, 'storyboard') != _.get(storyboardContext, 'id')) {
              const updatedWidget = {
                ...childwidget,
                actions: allowedActions.includes(getActionType(childwidget)) ? childwidget.actions :  []
              }

              return updatedWidget;
            }
          }
          return childwidget;
        })

        widget = {
          ...widget,
          widgets: children
        }
        return widget;

      } else {
        if(_.get(widget, 'actions') && _.get(widget, 'actions') !== null) {
          if(_.get(request, 'storyboard') != _.get(storyboardContext, 'id')) {
            widget = {
              ...widget,
              actions: allowedActions.includes(getActionType(widget)) ? widget.actions : [],
            }

            if (widget.type === TEXT){
              widget = {
                ...widget,
                content: allowedActions.includes(getActionType(widget)) ? widget.content : widget.content.replace(/<a[^>]*>(.*?)<\/a>/g, '$1')
              };
            }

            return widget;
          }
        }
        return widget;
      }
    })

    if(_.size(widgets) === 0){
      return null;
    }

    var processedWidgets = widgets.map(widget => {
      var layout = _.get(widget, 'properties.desktop.layout') || {};
      var stretched = _.get(widget, 'properties.desktop.stretched') || null;
      var inlineStretched = _.get(widget, 'properties.desktop.display') || null;
      const positon = new Frame(layout);
      const value = positon.get("transform", "translate").split(",").map(str => {
        str = str.trim();
        return parseFloat(str);
      });

      if (storyboardType === 'INTERACTIVE_CONTENT' && (stretched || inlineStretched === 'STRETCHED') && widget.type !== CONTENT) {
        return;
      }

      const actionType = _.get(widget, 'actions.0.type',null);
      if (actionType && actionType === 'page' && entityType === 'GATING'){
        widget.actions = [];
        if (widget.content && typeof widget.content === 'string' && widget.type === TEXT) {
          widget.content = widget.content.replace(/<a[^>]*>(.*?)<\/a>/g, '$1');
        }
      }

      const cleanWidgetActions = (widget, options = {}) => {
        const {
          allowedActions = [],
          disallowedActions = [],
          cleanTextLinks = true,
        } = options;
      
        const cleanActions = (w) => {
          if (!w) return;

          const actions = w.actions || [];
          const actionType = _.last(actions)?.type;
      
          // Clean TEXT widget <a> tags if the action isn't allowed or is disallowed
          if (
            w.type === TEXT &&
            cleanTextLinks &&
            ((allowedActions.length && !allowedActions.includes(actionType)) ||
              (disallowedActions.length && disallowedActions.includes(actionType)))
          ) {
            w.content = w.content.replace(/<a[^>]*>(.*?)<\/a>/g, '$1');
          }
      
          // Remove actions based on allowed/disallowed list
          if (w.actions) {
            if (
              (allowedActions.length && !allowedActions.includes(actionType)) ||
              (disallowedActions.length && disallowedActions.includes(actionType))
            ) {
              w.actions = [];
            }
          }
        };
      
        const cleanMenuActions = (menuWidget) => {
          if (!menuWidget) return;
      
          const actionType = menuWidget.action?.type;
          if (
            menuWidget.action &&
            ((allowedActions.length && !allowedActions.includes(actionType)) ||
              (disallowedActions.length && disallowedActions.includes(actionType)))
          ) {
            menuWidget.action = null;
          }
      
          if (_.size(menuWidget.children) > 0) {
            menuWidget.children.forEach(cleanMenuActions);
          }
        };

        if(widget.actions) {
          cleanActions(widget);
        }

        widget?.widgets?.forEach((child) => {
          cleanActions(child);

          if (child.type === MENU_CONTAINER || child.type === MENU_DRAGGABLE_CONTAINER) {
            child.content?.forEach(cleanMenuActions);
          }
      
          child.widgets?.forEach(cleanActions);
        });
      };
      
      if (request?.storyboard !== currentStoryboardId) {
        cleanWidgetActions(widget, {
          allowedActions: ['link', 'pageAnchor'],
        });
      }

      if ((request.pageId && request.pageId !== pageId) || (request?.activePageSection !== pageWidget && storyboardType === 'INTERACTIVE_CONTENT')) {
        cleanWidgetActions(widget, {
          disallowedActions: ['carousel'],
        });
      }

      if (variableMappingsIgnoreTypes.includes(entityType) || request?.storyboard !== currentStoryboardId){
        removeVariableMappingsFromWidget(widget);
      }

      if(widget.properties.desktop.llmProperty && (request?.storyboard !== currentStoryboardId)){
        widget.properties.desktop.llmProperty = null;
      }

      if(widget.type === ASSET){
        widget.content = null;
        
        if(widget.properties.desktop.type && _.includes(["VIDEO"], widget.properties.desktop.type)){
          widget.properties.desktop.video = null;
        }

        if(widget.properties.desktop.background && _.includes(["image", "video"], widget.properties.desktop.background.type)){
          widget.properties.desktop.background = null;
        }
      }

      if(widget.type === CONTENT){
        delete widget.id;
        widget.content = {};

        widget.properties.desktop.filters = false;
        widget.properties.desktop.search = false;
        widget.properties.mobile.filters = false;
        widget.properties.mobile.search = false;

        widget.properties.desktop.layoutStyle.itemsPerPage = 3;
        
        var contentWidgets = widget.widgets.filter(item => !_.includes([CONTENT_CARD], item.type));

        contentWidgets.map(child => {
          if([CONTENT_FILTER].includes(child.type)){
              var filterChildren = child.widgets.map(filterChild => {
                if(filterChild.type === CONTENT_FILTER_CONTAINER){
                  filterChild.content = [];
                }
                if(filterChild.type === CONTENT_SEARCH){
                  filterChild.content = null
                }
                return filterChild;
              });
              child.widgets = filterChildren;
            }
          })

        widget.widgets = contentWidgets;

        if(widget.properties.desktop.background && _.includes(["image", "video"], widget.properties.desktop.background.type)){
          widget.properties.desktop.background = null;
        }
      }

      isAssetIsChild(widget);

      function getElementOffsetTop(element) {
        let top = 0;
        while (element) {
          top += element.offsetTop;
          element = element.offsetParent;
        }
        return top - 255;
      }

      if(entityType === "GATING"){
        const x = value[0] - originalLeft + left + 20 * offset;
        const y = value[1] - originalTop + top  + 20 * offset;
        positon.set('transform', 'translate', `${x}px, ${y}px`);
      }

      else  {
        if (storyboardType === 'INTERACTIVE_CONTENT' || storyboardType === 'SPATIAL') {
          const x = value[0] - originalLeft + left;
          const y = value[1] - originalTop + top + 20 * offset;
          positon.set('transform', 'translate', `${x}px, ${y}px`);
        }

        if(storyboardType === 'LANDING_PAGE' || storyboardType === 'RESOURCE_CENTER'){
        const selectPageContainer = document.querySelector(`#${editorContext.editorSession.id} .editor_widget_scroll_container .editor-page-wrapper #${activePageSection}`);
        const pageRect = selectPageContainer.getBoundingClientRect();

        let pageoffset = 0;
      
        if(activePageSection && selectPageContainer){ 
          pageoffset = scrollContainer.scrollTop -  getElementOffsetTop(selectPageContainer) ;
        }
        const x = value[0] - originalLeft + left + 0 * offset;
        let y = pageoffset + 40 * offset;

          if (activePageSection && (pageRect.height < y || y < 0)) {
            y = pageRect.height / 2;
          }
        positon.set('transform', 'translate', `${x}px, ${y}px`);
      }
    }

      widget = _.set(widget, 'properties.desktop.layout', positon.get());
      return widget;

    }).sort((w1, w2) => {
      const o1 = _.get(w1, 'order') || 1;
      const o2 = _.get(w2, 'order') || 1;
  
      return o1 - o2;
    });

    pasteStateReference.current = {
      page: pageWidget,
      scrollPosition: currentScrollPosition,
      offset: offset + 1
    };

    return processedWidgets;
  }

  const viewportPositionChanged = (current, previous) => {
    if(!previous){
      return null;
    }

    const currentTop = _.get(current, 'top', 0);
    const currentLeft = _.get(current, 'left', 0);

    const previousTop = _.get(previous, 'top', 0);
    const previousLeft = _.get(previous, 'left', 0);

    return (currentTop != previousTop || currentLeft != previousLeft);
  }

  return false;
}

const widgetTypesWithAsset = new Set([SECTION, CAROUSEL, IMAGE, VIDEO, BUTTON, SHAPE, LINE]);

function clearAssetContent(widget) {
  if (!widget || !widget.widgets) return;


  widget.widgets.forEach(child => {
    if (child?.type === ASSET) {
      child.content = null;
    } else if (child?.widgets) {
      child.widgets?.forEach(subChild => {
        if (subChild?.type === CAROUSEL_CARD) {
          subChild.widgets?.forEach(carouselChild => {
            if (carouselChild?.type === ASSET) {
              carouselChild.content = null;
            }
          });
        }
      });
    }
  });
}

function isAssetIsChild(widget) {
  if (widgetTypesWithAsset.has(widget?.type)) {
    widget.widgets?.forEach(child => {
      clearAssetContent(child);
    });
  }
}


function removeVariableMappingsFromWidget(request) {
    if(!request){
      return request;
    }

    if (request.type === TEXT){
      let content = request.content;
      content = content.replace(
        /<span[^>]*class="[^"]*\bvariable-wrapper\b[^"]*"[^>]*>(.*?)<\/span>/g,
        "$1"
      );
      content = content.replace(
        /<span[^>]*\sdata-variable-id=["'][^"']*["'][^>]*>(.*?)<\/span>/g,
        "$1"
      );      
      request.content = content;
      request.properties.desktop.variables = null;
    }

    const variableMappings = request.variableMappings;
    if (variableMappings && _.size(variableMappings) > 0) {
      request.variableMappings = [];
    }

    if(_.size(request.widgets) > 0){
      request.widgets.forEach(childRequest => {
        removeVariableMappingsFromWidget(childRequest);
      });
    }
}